import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AutoComplete, Button, Modal, Select, Table, Tooltip, message } from "antd";
import { Plus } from "lucide-react";
import axios from "axios";
import SshProxyOnboarding from "../../../pages/enduser/ssh-proxy/SshProxyOnboarding";

import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_PAM_API } from "../../../constants";
import { logOutUser } from "../../../common";
import { useDebounce } from "../../../common/debounce";
import { updateGatewayStatus } from "../../../service/sshProxyService";
import { isAdminUser } from "../../../utils/helpers";
import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";
import {
  CountChip,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  RowActionMenu,
  StatusPill,
  TableCard,
  anTableProps,
  tableEmpty,
  useScrollbarWhileScrolling,
} from "../../../design-system/tableUi";
import {
  HEARTBEAT_INTERVAL_SECONDS,
  formatRelativeAge,
  proxyLiveness,
} from "../../../pages/enduser/ssh-proxy/sshProxyModel";

// Gateways — the SSH proxies. One word for one object: the sidebar, this page
// and the detail page all say "gateway" now, where the codebase previously
// alternated between "jump server" and "SSH proxy" for the same row.

// One Status column, from two facts. `status` is the lifecycle: IN PROGRESS
// from registration until the gateway first checks in, ACTIVE after that, and
// DISABLED, the one state an admin sets by hand. An active gateway is then
// Online or Offline by its heartbeat (proxyLiveness's threshold).
//
// `server` is what the list is filtered by server-side (matched with ilike, so
// casing does not matter); Online and Offline are both "active" there. The
// list API has no liveness filter, so for those two the page loads every
// active gateway and filters and paginates on the client (see fetchGateways).
const GATEWAY_STATES = {
  online: { label: "Online", tone: "success", server: "active" },
  offline: { label: "Offline", tone: "warn", server: "active" },
  progress: { label: "In progress", tone: "info", server: "In progress" },
  disabled: { label: "Disabled", tone: "danger", server: "disabled" },
};

const STATUS_FILTER_OPTIONS = Object.entries(GATEWAY_STATES).map(([value, s]) => ({ value, label: s.label }));

// → { key, label, tone, tip }
function gatewayState(r) {
  const status = String(r.status || "").trim().toUpperCase();
  if (status === "DISABLED") return { key: "disabled", ...GATEWAY_STATES.disabled, tip: "Disabled by an administrator. Sessions through it are refused." };
  if (status === "IN PROGRESS") return { key: "progress", ...GATEWAY_STATES.progress, tip: "Registered; waiting for the gateway's first check-in." };
  const live = proxyLiveness(r.lastSeenAt);
  if (live.state === "live") {
    return { key: "online", ...GATEWAY_STATES.online, tip: `Last heartbeat ${formatRelativeAge(live.ageSeconds)}.` };
  }
  return {
    key: "offline",
    ...GATEWAY_STATES.offline,
    tip:
      live.state === "never"
        ? "This gateway has never checked in. That is usually SSHPROXY_CONTROL_URL being unset — SSH itself may still work, the console just never hears from the process."
        : `Last seen ${formatRelativeAge(live.ageSeconds)}. It heartbeats every ${HEARTBEAT_INTERVAL_SECONDS}s and goes offline after three missed beats.`,
  };
}

const PAGE_SIZE = 10;

// Online/Offline are decided from lastSeenAt on the client, so those filters
// need every active row: they are fetched in pages of LIVENESS_FETCH_SIZE
// (the API has no page-size cap) up to a sanity bound.
const LIVENESS_FILTERS = new Set(["online", "offline"]);
const LIVENESS_FETCH_SIZE = 200;
const LIVENESS_MAX_PAGES = 50;

// The proxy heartbeats every 60s. Re-listing on the same cadence is what makes
// a stopped proxy go stale on screen without anybody reloading the page.
const REFRESH_MS = HEARTBEAT_INTERVAL_SECONDS * 1000;

const Gateways = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  useScrollbarWhileScrolling();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedValue = useDebounce(search, 500);
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [, setTick] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [onboarding, setOnboarding] = useState(false);
  const [pendingDisable, setPendingDisable] = useState(null);
  const isAdmin = isAdminUser();
  const livenessFilter = LIVENESS_FILTERS.has(statusFilter);
  // With a liveness filter every row is already loaded, so changing page must
  // not refetch; otherwise the server pages as normal.
  const serverPage = livenessFilter ? 1 : page;

  const fetchGateways = useCallback(() => {
    setIsLoading(true);
    const listPage = (pageId, pageSize) =>
      axios
        .post(
          `${REACT_APP_PAM_API}/jumpserver/listJumpServer`,
          {
            pageId,
            pageSize,
            search: debouncedValue,
            filter: { filterBy: statusFilter ? "status" : "", value: GATEWAY_STATES[statusFilter]?.server || "" },
            token: AUTH_TOKEN(),
            orgId: ORG_ID(),
            domainId: DOMAIN_ID(),
          },
          { headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true } },
        )
        .then((res) => ({ rows: res?.data?.jumpServers || [], total: res?.data?.total ?? 0 }));

    // Every active row, page by page, de-duplicated by id (the query has no
    // ORDER BY, so a row can in principle appear on two pages).
    const listAll = async () => {
      const byId = new Map();
      let total = 0;
      for (let pageId = 1; pageId <= LIVENESS_MAX_PAGES; pageId += 1) {
        const res = await listPage(pageId, LIVENESS_FETCH_SIZE);
        total = res.total;
        res.rows.forEach((r) => byId.set(r.id ?? r.serverName, r));
        if (res.rows.length < LIVENESS_FETCH_SIZE || byId.size >= total) break;
      }
      return { rows: [...byId.values()], total };
    };

    (livenessFilter ? listAll() : listPage(serverPage, PAGE_SIZE))
      .then((res) => {
        setIsLoading(false);
        setRows(res.rows);
        setTotalCount(res.total);
      })
      .catch((err) => {
        setIsLoading(false);
        if (err?.response?.data?.message === "Invalid credentials" || err?.response?.status === 401) {
          logOutUser();
        } else {
          messageApi.error("Unable to fetch gateways");
        }
      });
  }, [serverPage, livenessFilter, debouncedValue, statusFilter, messageApi]);

  useEffect(() => {
    fetchGateways();
  }, [fetchGateways]);

  // Poll on the heartbeat cadence. `tick` also forces a re-render between
  // fetches so "Last seen 2m ago" keeps counting up rather than freezing at
  // whatever it said when the response landed.
  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
      fetchGateways();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchGateways]);

  // Online and Offline come from lastSeenAt, so they can't be told apart
  // server-side: `rows` then holds every active gateway, and the match is
  // filtered and paginated here so the count and pages stay right.
  const matching = useMemo(
    () => (livenessFilter ? rows.filter((r) => gatewayState(r).key === statusFilter) : rows),
    [rows, statusFilter, livenessFilter],
  );
  const shownTotal = livenessFilter ? matching.length : totalCount;
  const visible = useMemo(
    () => (livenessFilter ? matching.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : matching),
    [matching, livenessFilter, page],
  );

  // A gateway can change state between polls; don't strand the user on a page
  // past the end of the filtered list.
  useEffect(() => {
    if (!livenessFilter) return;
    const lastPage = Math.max(1, Math.ceil(matching.length / PAGE_SIZE));
    if (page > lastPage) setPage(lastPage);
  }, [livenessFilter, matching.length, page]);

  const onlineCount = useMemo(() => rows.filter((r) => gatewayState(r).key === "online").length, [rows]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    axios
      .post(
        `${REACT_APP_PAM_API}/jumpserver/deleteJumpServer`,
        { id: pendingDelete.id, token: AUTH_TOKEN(), orgId: ORG_ID() },
        { headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true } },
      )
      .then(() => {
        messageApi.success("Gateway deleted");
        setPendingDelete(null);
        fetchGateways();
      })
      .catch((error) => {
        setPendingDelete(null);
        if (error?.response?.data?.message === "Invalid credentials" || error?.response?.status === 401) {
          logOutUser();
        } else {
          messageApi.error("Unable to delete gateway");
        }
      });
  };

  // Admin-only. Disabling cuts the gateway off at once, so it is confirmed
  // first; enabling just puts it back to ACTIVE.
  const setGatewayStatus = (row, status) =>
    updateGatewayStatus({
      id: row?.id,
      status,
      onSuccess: () => {
        setPendingDisable(null);
        messageApi.success(status === "DISABLED" ? "Gateway disabled" : "Gateway enabled");
        fetchGateways();
      },
      onFailure: (err) => {
        setPendingDisable(null);
        const code = err?.response?.status;
        if (code === 401) return logOutUser();
        if (code === 403) return messageApi.error("Administrator role required");
        const detail = err?.response?.data?.error || err?.response?.data?.message;
        return messageApi.error(detail || `Unable to ${status === "DISABLED" ? "disable" : "enable"} the gateway`);
      },
    });

  const columns = [
    {
      title: "Gateway",
      dataIndex: "serverName",
      key: "serverName",
      render: (_, r) => (
        <Link to={`/pam/jumpServer/${r.id}`} style={{ fontWeight: 500, color: AN_COLORS.ink }}>
          {r.serverName || "—"}
        </Link>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 150,
      render: (_, r) => {
        const st = gatewayState(r);
        return (
          <Tooltip title={st.tip}>
            <span>
              <StatusPill tone={st.tone} label={st.label} status={st.key} />
            </span>
          </Tooltip>
        );
      },
    },
    { title: "Public IP", key: "publicIpAddress", width: 140, render: (_, r) => <MonoCell>{r.publicIpAddress}</MonoCell> },
    { title: "Listening on", key: "proxyListen", width: 120, render: (_, r) => <MonoCell>{r.proxyListen}</MonoCell> },
    {
      title: "",
      key: "action",
      width: 56,
      render: (_, r) => (
        <RowActionMenu
          items={[
            { key: "open", label: <Link to={`/pam/jumpServer/${r.id}`}>Open</Link> },
            // Fleet onboarding: one command enrols many machines behind this gateway.
            ...(isAdmin ? [{ key: "enrol", label: <Link to={`/pam/jumpServer/enrol?gateway=${r.id}`}>Enrol machines</Link> }] : []),
            ...(isAdmin
              ? String(r.status || "").toUpperCase() === "DISABLED"
                ? [{ key: "enable", label: "Enable", onClick: () => setGatewayStatus(r, "ACTIVE") }]
                : [{ key: "disable", label: "Disable", onClick: () => setPendingDisable(r) }]
              : []),
            { type: "divider" },
            { key: "delete", danger: true, label: "Delete", onClick: () => setPendingDelete(r) },
          ]}
        />
      ),
    },
  ];

  const applied = [
    statusFilter && { key: "status", label: `Status: ${GATEWAY_STATES[statusFilter]?.label}`, clear: () => setStatusFilter(null) },
    debouncedValue && { key: "search", label: `Name: ${debouncedValue}`, clear: () => setSearch("") },
  ].filter(Boolean);

  return (
    <PageShell>
      {contextHolder}

      <PageHeader
        eyebrow="PAM · Access Control"
        title="Gateways"
        subtitle="Operators connect through the gateway with their name in the connection string, or from the console's terminal."
        chips={
          <>
            <CountChip count={shownTotal} label={shownTotal === 1 ? "Gateway" : "Gateways"} />
            <CountChip count={onlineCount} label="Online" />
          </>
        }
        actions={
          <Button type="primary" icon={<Plus size={13} strokeWidth={2.2} />} onClick={() => setOnboarding(true)}>
            Onboard gateway
          </Button>
        }
      />

      <FilterCard
        columns="1.6fr 1fr"
        applied={applied}
        onClearAll={() => {
          setStatusFilter(null);
          setSearch("");
        }}
        showing={visible.length}
        total={shownTotal}
      >
        <FilterField label="Search name">
          <AutoComplete
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            allowClear
            placeholder="prod-bastion"
            style={{ width: "100%", height: 36 }}
            options={rows.map((r) => ({ value: r.serverName }))}
            filterOption={(input, option) =>
              String(option?.value || "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </FilterField>

        <FilterField label="Status">
          <Select
            allowClear
            placeholder="Any status"
            style={{ width: "100%", height: 36 }}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v ?? null);
              setPage(1);
            }}
            options={STATUS_FILTER_OPTIONS}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={visible}
          rowKey={(r) => r.id ?? r.serverName}
          loading={isLoading}
          locale={tableEmpty(isLoading, applied.length ? "No gateways match your filters." : "No gateways yet.")}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: shownTotal,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      </TableCard>

      {/* Onboarding runs over the list as a form, the same shape as Add
          Endpoint. The /pam/jumpServer/onboard route still exists for deep
          links — the Linux endpoint flow points at it. */}
      <Modal
        open={onboarding}
        onCancel={() => setOnboarding(false)}
        footer={null}
        width={900}
        destroyOnClose
        title="Onboard a Gateway"
        styles={{ body: { maxHeight: "70vh", overflowY: "auto", paddingTop: 8 } }}
      >
        <SshProxyOnboarding
          embedded
          onDone={(newId) => {
            setOnboarding(false);
            fetchGateways();
            if (newId) navigate(`/pam/jumpServer/${newId}`);
          }}
        />
      </Modal>

      <Modal
        title="Delete gateway"
        open={!!pendingDelete}
        onOk={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <p style={{ fontFamily: AN_FONT }}>
          Deleting <strong>{pendingDelete?.serverName || "this gateway"}</strong> removes its configuration. Operators
          connecting through it will be refused.
        </p>
      </Modal>

      <Modal
        title="Disable gateway"
        open={!!pendingDisable}
        onOk={() => setGatewayStatus(pendingDisable, "DISABLED")}
        onCancel={() => setPendingDisable(null)}
        okText="Disable"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <p style={{ fontFamily: AN_FONT }}>
          Disable <strong>{pendingDisable?.serverName || "this gateway"}</strong>? Sessions through this gateway will stop.
        </p>
      </Modal>
    </PageShell>
  );
};

export default Gateways;
